/**
 * Product Search Service
 * 
 * Searches for real purchasable products matching a query.
 * Returns simple product listing data (title, image, price, source, link).
 */

import { supabase } from '@/integrations/supabase/client';

// Simple product listing item
export interface ProductItem {
  title: string;
  image_url: string | null;
  price: string | null;
  currency: string | null;
  source: string | null;
  product_url: string | null;
}

// Search input
export interface ProductSearchRequest {
  query: string;
  category?: string;
  region: string;
}

// Search output
export interface ProductSearchResponse {
  products: ProductItem[];
}

// Type guard to check if response is valid product search format
export function isValidProductSearchResponse(data: unknown): data is ProductSearchResponse {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.products)) return false;
  
  // Check first product has correct shape (simple: title, product_url required)
  if (obj.products.length > 0) {
    const first = obj.products[0] as Record<string, unknown>;
    // Must have title at minimum
    if (typeof first.title !== 'string') return false;
    // Should NOT have competitor-specific fields
    if ('approach' in first || 'targetAudience' in first || 'whatItSolves' in first) {
      return false;
    }
  }
  
  return true;
}

// Error type for wrong response format
export class WrongResponseTypeError extends Error {
  constructor() {
    super('Wrong response type received. Expected product listing format.');
    this.name = 'WrongResponseTypeError';
  }
}

/**
 * Search for products matching query
 */
export async function searchProducts(input: ProductSearchRequest): Promise<ProductSearchResponse> {
  const { data, error } = await supabase.functions.invoke('search-products', {
    body: input,
  });

  if (error) {
    console.error('Product search error:', error);
    throw new Error(error.message || 'Failed to search products');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  // Validate response format
  if (!isValidProductSearchResponse(data)) {
    console.error('Invalid product search response:', data);
    throw new WrongResponseTypeError();
  }

  return data;
}
